import { useNow } from '@lib/hooks/useNow';
import { SocialPost } from '@lib/social/scheduling/posts';
import { calendarGrid, monthRange } from '@lib/social/scheduling/window';
import { Skeleton } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { scheduleSettings } from '@stores/ScheduleSettings';
import { socialScheduleStore } from '@stores/SocialSchedule';
import { addDays, format, isSameDay, isSameMonth, startOfDay } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, type Ref } from 'react';
import pageCss from '../../SchedulePage.module.scss';
import { PostRow } from '../PostRow/PostRow';
import css from './ScheduleAgenda.module.scss';

/** "Today" and "Tomorrow" come from the page's `now`, the same clock as the calendar's ring. */
const dayHeading = (day: Date, now: Date): string => {
  const date = format(day, 'EEEE, MMMM d');
  if (isSameDay(day, now)) return `Today · ${date}`;
  if (isSameDay(day, addDays(now, 1))) return `Tomorrow · ${date}`;
  return date;
};

/**
 * Posts by day in the schedule's zone, days ascending, as `[dayMidnight,
 * posts]`. Input is already soonest-first. `today`, when given, gets a section even with no
 * posts: it is where the now line lives.
 */
function groupByDay(posts: SocialPost[], today: Date | null): [Date, SocialPost[]][] {
  const groups: [Date, SocialPost[]][] = [];
  for (const post of posts) {
    const day = startOfDay(scheduleSettings.inZone(post.publishAt));
    const last = groups[groups.length - 1];
    if (last && last[0].getTime() === day.getTime()) last[1].push(post);
    else groups.push([day, [post]]);
  }
  if (today && !groups.some(([day]) => day.getTime() === today.getTime())) {
    const next = groups.findIndex(([day]) => day.getTime() > today.getTime());
    groups.splice(next === -1 ? groups.length : next, 0, [today, []]);
  }
  return groups;
}

/** The line between what has had its time and what has not, labelled at its left end. */
const NowLine = ({ ref }: { ref: Ref<HTMLDivElement> }) => (
  <div ref={ref} className={css.nowLine} role="separator" aria-label="Now">
    <span className={css.nowLabel}>Now</span>
  </div>
);

/**
 * The visible month as a Google Calendar agenda: a date heading, then that
 * day's posts with their times. Reads the same request the calendar made
 * for the month's grid, filtered to the month's own days.
 */
export const ScheduleAgenda = observer(function ScheduleAgenda({
  month,
  now,
}: {
  month: Date;
  now: Date;
}) {
  const grid = calendarGrid(month);
  const state = socialScheduleStore.rangeState(grid.range);
  const posts = socialScheduleStore.postsBetween(monthRange(month));
  // Ticking, unlike the page's `now`: the line moves past a post when its
  // time comes. The headings stay on the frozen clock, so a tab left open
  // past midnight keeps yesterday's "Today" heading with the line at its
  // foot until a reload, the same tradeoff that clock already makes.
  const nowMs = useNow();
  const today = isSameMonth(now, month) ? startOfDay(now) : null;

  // Once per mount, when the list first has rows: a month's worth of past
  // posts would otherwise put the line below the fold. The agenda remounts
  // per month and per view switch, so each landing on the list finds it.
  const lineRef = useRef<HTMLDivElement>(null);
  const loaded = state?.loaded === true;
  useEffect(() => {
    lineRef.current?.scrollIntoView({ block: 'center' });
  }, [loaded]);

  if (state?.error && !state.loaded) {
    return (
      <div className={pageCss.stateRow}>
        <span>Something went wrong. Try again later.</span>
        <ActionButton variant="secondary" onClick={() => void socialScheduleStore.load(grid.range)}>
          Retry
        </ActionButton>
      </div>
    );
  }

  if (!state?.loaded) {
    return (
      <div className={css.list}>
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={64}
            sx={{ borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.04)' }}
          />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className={pageCss.stateRow}>Nothing scheduled in {format(month, 'MMMM yyyy')}.</div>
    );
  }

  return (
    <div className={css.list}>
      {groupByDay(posts, today).map(([day, dayPosts]) => {
        const isToday = today !== null && day.getTime() === today.getTime();
        // Other days are wholly before or after now; only today's section splits.
        const pastCount = isToday ? dayPosts.filter((p) => p.publishAt <= nowMs).length : 0;
        return (
          <section key={day.getTime()} className={css.day}>
            <h3 className={css.dayHeading}>{dayHeading(day, now)}</h3>
            {dayPosts.slice(0, pastCount).map((post) => (
              <PostRow key={post.id} post={post} />
            ))}
            {isToday && dayPosts.length === 0 && (
              <p className={css.dayEmpty}>Nothing to post today.</p>
            )}
            {isToday && <NowLine ref={lineRef} />}
            {dayPosts.slice(pastCount).map((post) => (
              <PostRow key={post.id} post={post} />
            ))}
          </section>
        );
      })}
    </div>
  );
});
